import * as Utils from "../Utils";
import { adminFetch, buildQueryString } from "./admin_api";

const REPLY_WINDOW_MS = 24 * 60 * 60 * 1000;

const getAuthToken = () => localStorage.getItem("adminToken");

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

const whatsappApi = (path, options = {}) =>
  adminFetch(`${Utils.API_URL}v1/whatsapp${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

export const formatWaId = (waId) => {
  const digits = String(waId || "").replace(/\D/g, "");
  if (!digits) return "Unknown";

  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }

  return `+${digits}`;
};

export const getDisplayName = (contactName, waId) => {
  const name = String(contactName || "").trim();
  if (name) return name;
  return formatWaId(waId);
};

export const normalizeConversationList = (response) => {
  const conversations = response?.data?.conversations;
  return Array.isArray(conversations) ? conversations : [];
};

export const normalizeConversationPagination = (response) => response?.data?.pagination || null;

export const normalizeMessageList = (response) => {
  const messages = response?.data?.messages;
  return Array.isArray(messages) ? messages : [];
};

export const normalizeMessagePagination = (response) => response?.data?.pagination || null;

export const getLastInboundMessage = (messages = []) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.direction === "inbound") {
      return messages[i];
    }
  }
  return null;
};

export const getReplyWindowState = (messages = []) => {
  const lastInbound = getLastInboundMessage(messages);
  if (!lastInbound?.createdAt) {
    return {
      canReply: false,
      remainingMs: 0,
      expiresAt: null,
      reason: "no_inbound",
    };
  }

  const startedAt = new Date(lastInbound.createdAt).getTime();
  const expiresAt = startedAt + REPLY_WINDOW_MS;
  const remainingMs = expiresAt - Date.now();

  return {
    canReply: remainingMs > 0,
    remainingMs: Math.max(0, remainingMs),
    expiresAt: new Date(expiresAt),
    reason: remainingMs > 0 ? null : "expired",
  };
};

export const formatReplyWindowRemaining = (remainingMs) => {
  if (!remainingMs || remainingMs <= 0) return "Expired";

  const totalMinutes = Math.floor(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }

  return `${minutes}m left`;
};

export const getMessageStatusLabel = (message) => {
  if (message?.direction === "inbound") return "Received";

  switch (getOutboundMessageTickState(message)) {
    case "auto_replied":
      return "Auto-reply";
    case "failed":
      return "Failed";
    case "read":
      return "Read";
    case "delivered":
      return "Delivered";
    case "sent":
      return "Sent";
    default:
      return message?.status || "Outbound";
  }
};

export const getOutboundMessageTickState = (message) => {
  const status = String(message?.status || "").toLowerCase();

  if (status === "failed") return "failed";
  if (status === "auto_replied") return "auto_replied";
  if (status === "read" || status === "seen" || message?.readAt) return "read";
  if (status === "delivered") return "delivered";
  if (status === "sent" || status === "queued" || status === "pending") return "sent";
  return "delivered";
};

export async function listWhatsAppConversations(params = {}) {
  return whatsappApi(`/admin/conversations${buildQueryString(params)}`);
}

export async function getWhatsAppConversationMessages(waId, params = {}) {
  return whatsappApi(`/admin/conversations/${encodeURIComponent(waId)}/messages${buildQueryString(params)}`);
}

export async function replyToWhatsAppConversation(waId, message) {
  return whatsappApi(`/admin/conversations/${encodeURIComponent(waId)}/reply`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

export async function replyToWhatsAppConversationWithMedia(waId, { message = "", file }) {
  const formData = new FormData();
  if (message.trim()) {
    formData.append("message", message.trim());
  }
  formData.append("file", file);

  return adminFetch(`${Utils.API_URL}v1/whatsapp/admin/conversations/${encodeURIComponent(waId)}/reply`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${getAuthToken()}`,
    },
    body: formData,
  });
}

export const getMessageMediaUrl = (message) => {
  const url = message?.mediaUrl || message?.attachmentUrl || message?.media?.url || message?.url;
  return url ? String(url) : "";
};

export const isImageMessage = (message) => {
  const type = String(message?.messageType || message?.type || "").toLowerCase();
  if (type.includes("image")) return true;
  const url = getMessageMediaUrl(message);
  return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url);
};

const LEGACY_UNSUPPORTED_BODY = /^\[unsupported message received\]$/i;

const MESSAGE_TYPE_META = {
  image: { icon: "ri-image-line", label: "Photo" },
  video: { icon: "ri-video-line", label: "Video" },
  audio: { icon: "ri-mic-line", label: "Voice message" },
  document: { icon: "ri-file-text-line", label: "Document" },
  sticker: { icon: "ri-sticky-note-line", label: "Sticker" },
  location: { icon: "ri-map-pin-line", label: "Location" },
  contacts: { icon: "ri-contacts-line", label: "Contact" },
  reaction: { icon: "ri-emotion-line", label: "Reaction" },
  interactive: { icon: "ri-cursor-line", label: "Reply" },
  button: { icon: "ri-cursor-line", label: "Button reply" },
  order: { icon: "ri-shopping-bag-line", label: "Order" },
  system: { icon: "ri-information-line", label: "System" },
  unsupported: { icon: "ri-error-warning-line", label: "Unsupported" },
  unknown: { icon: "ri-question-line", label: "Message" },
};

export const getMessageTypeMeta = (message) => {
  const type = String(message?.messageType || message?.type || "").toLowerCase();
  return MESSAGE_TYPE_META[type] || null;
};

export const isSpecialMessageType = (message) => {
  const type = String(message?.messageType || message?.type || "").toLowerCase();
  return Boolean(MESSAGE_TYPE_META[type] && type !== "text");
};

export const getMessageDisplayBody = (message) => {
  const raw = String(message?.body || "").trim();
  const type = String(message?.messageType || message?.type || "").toLowerCase();

  if (type === "unsupported" || LEGACY_UNSUPPORTED_BODY.test(raw)) {
    if (raw && !LEGACY_UNSUPPORTED_BODY.test(raw)) return raw;
    return "WhatsApp couldn't display this message. Ask the customer to resend as text.";
  }

  if (raw) return raw;

  const meta = MESSAGE_TYPE_META[type];
  if (meta) return `[${meta.label}]`;
  return type ? `[${type}]` : "[message]";
};

export const normalizeInboxNotifications = (response) => ({
  unreadCount: Number(response?.data?.unreadCount) || 0,
  recentUnread: Array.isArray(response?.data?.recentUnread)
    ? response.data.recentUnread
    : [],
});

export async function getWhatsAppInboxNotifications(params = {}) {
  return whatsappApi(`/admin/notifications${buildQueryString(params)}`);
}

export async function markWhatsAppConversationRead(waId) {
  return whatsappApi(`/admin/conversations/${encodeURIComponent(waId)}/read`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function markAllWhatsAppInboxRead() {
  return whatsappApi("/admin/notifications/read-all", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export const getNotificationPreview = (body, maxLength = 80) => {
  const text = getMessageDisplayBody({ body }).trim();
  if (!text) return "New WhatsApp message";
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
};
